/**
 * @vuelto-surface
 * mobile: n/a
 * desktop: ok
 * strategy: inherit
 */
import React from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SURFACE_CARD_RADIUS_CONTENT } from '../../../../shared/surface-cards/surfaceCards.styles';
import { ticketColors } from '../../../../shared/theme/ticketColors'
import { spacing } from '../../../../shared/theme/spacing'
import { typography } from '../../../../shared/theme/typography';
import { ticketText } from '../../../../shared/ticket/ticketText';
import { ModuleEmptyState } from '../../../../shared/ui/ModuleEmptyState';
import { formatLocalDate } from '@shared/vueltoDateTime';
import type { EmployeeRecord } from '../../../store/useEmployeesStore';
import type { EmployeePermissionsVm } from './useEmployeePermissions';
import { resolveBranchDisplayName } from './useEmployeePermissions';
import { summarizeEmployeeModuleAccess } from './permissionUiLabels';
import { invitationRoleLabel } from './EmployeeRolePresets';

type Props = {
  vm: EmployeePermissionsVm;
};

export function EmployeePermissionsTable({ vm }: Props) {
  const {
    filteredActiveEmployees,
    branchNameById,
    onOpenDetail,
    sortBy,
    setSortBy,
    visibleModules,
    branchFilter,
  } = vm;

  if (filteredActiveEmployees.length === 0) {
    return (
      <ModuleEmptyState
        title="Sin resultados"
        subtitle="No hay empleados activos con estos filtros."
        testID="perfil.team.permissions.no-results"
      />
    );
  }

  const handleSort = () => {
    if (!setSortBy) return;
    setSortBy(sortBy === 'name-asc' ? 'name-desc' : 'name-asc');
  };

  const renderSortIcon = () => {
    if (sortBy === 'name-asc') return <Ionicons name="arrow-up" size={11} color={ticketColors.gold} style={styles.sortIcon} />;
    if (sortBy === 'name-desc') return <Ionicons name="arrow-down" size={11} color={ticketColors.gold} style={styles.sortIcon} />;
    return null;
  };

  return (
    <View style={styles.tableOuter}>
      <View style={styles.table}>
        <View style={[styles.row, styles.headerRow]}>
          <View style={styles.colEmpleado}>
            <TouchableOpacity
              style={styles.sortableHeader}
              onPress={handleSort}
              accessibilityRole="button"
              accessibilityLabel="Ordenar por nombre"
            >
              <Text style={[styles.headerCell, (sortBy === 'name-asc' || sortBy === 'name-desc') && styles.headerCellActive]}>
                Empleado
              </Text>
              {renderSortIcon()}
            </TouchableOpacity>
          </View>
          <View style={styles.colRol}>
            <Text style={styles.headerCell}>Rol</Text>
          </View>
          <View style={styles.colSucursales}>
            <Text style={styles.headerCell}>Sucursales</Text>
          </View>
          <View style={styles.colEmail}>
            <Text style={styles.headerCell}>Email</Text>
          </View>
          <View style={styles.colEstado}>
            <Text style={styles.headerCell}>Estado</Text>
          </View>
        </View>

        {filteredActiveEmployees.map((employee: EmployeeRecord) => {
          const assignmentCount = employee.branchAssignments.length;
          const branchNames = employee.branchAssignments
            .map((a) => resolveBranchDisplayName(branchNameById, a.branchId, a.branchName ?? ''))
            .filter(Boolean)
            .join(', ');
          const moduleSummary = summarizeEmployeeModuleAccess(
            employee,
            visibleModules,
            branchFilter
          );

          return (
            <TouchableOpacity
              key={employee.id}
              style={styles.row}
              onPress={() => onOpenDetail(employee.id)}
              accessibilityRole="button"
              accessibilityLabel={`Ver detalle de permisos de ${employee.name}`}
              testID="perfil.team.permissions.employee-row"
            >
              <View style={styles.colEmpleado}>
                <Text style={styles.empleadoName} numberOfLines={1}>{employee.name}</Text>
                {employee.createdAt ? (
                  <Text style={styles.empleadoMeta} numberOfLines={1}>
                    Alta {formatLocalDate(employee.createdAt)}
                  </Text>
                ) : null}
              </View>
              <View style={styles.colRol}>
                <Text style={styles.rolText} numberOfLines={1}>{invitationRoleLabel(employee.role)}</Text>
                <Text style={styles.moduleSummaryText} numberOfLines={1}>
                  {moduleSummary}
                </Text>
              </View>
              <View style={styles.colSucursales}>
                <Text style={styles.sucursalesCount} numberOfLines={1}>
                  {assignmentCount === 1 ? '1 sucursal' : `${assignmentCount} sucursales`}
                </Text>
                {branchNames ? (
                  <Text style={styles.sucursalesNames} numberOfLines={1}>{branchNames}</Text>
                ) : null}
              </View>
              <View style={styles.colEmail}>
                <Text style={styles.emailText} numberOfLines={1}>
                  {employee.email ?? '—'}
                </Text>
              </View>
              <View style={styles.colEstado}>
                <Text style={ticketText.statusOk}>Activo</Text>
                <Ionicons name="chevron-forward" size={16} color={ticketColors.ink3} style={{ marginLeft: spacing.xs }} />
              </View>
            </TouchableOpacity>
          );
        })}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  tableOuter: {
    width: '100%',
    alignSelf: 'stretch',
  },
  table: {
    width: '100%',
    borderRadius: SURFACE_CARD_RADIUS_CONTENT,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    overflow: 'hidden',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 52,
    paddingVertical: spacing.sm,
    paddingLeft: spacing.md,
    paddingRight: spacing.md,
    gap: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.rule,
  },
  headerRow: {
    minHeight: 40,
    backgroundColor: ticketColors.paper2,
  },
  headerCell: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '900',
    textTransform: 'uppercase',
    letterSpacing: 0.35,
    fontSize: 11,
  },
  headerCellActive: {
    color: ticketColors.gold,
  },
  sortableHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  sortIcon: {
    marginLeft: 2,
  },
  colEmpleado: {
    flex: 1,
    minWidth: 120,
    gap: 2,
    paddingRight: spacing.xs,
  },
  colRol: {
    width: 100,
    flexShrink: 0,
    justifyContent: 'center',
  },
  colSucursales: {
    width: 140,
    flexShrink: 0,
    gap: 2,
  },
  colEmail: {
    width: 160,
    flexShrink: 0,
    justifyContent: 'center',
  },
  colEstado: {
    width: 100,
    flexShrink: 0,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  empleadoName: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '800',
  },
  empleadoMeta: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '600',
    fontSize: 11,
  },
  rolText: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '600',
    fontSize: 13,
  },
  moduleSummaryText: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontSize: 11,
  },
  sucursalesCount: {
    ...typography.caption,
    color: ticketColors.ink,
    fontWeight: '700',
    fontSize: 12,
  },
  sucursalesNames: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontSize: 11,
  },
  emailText: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '600',
  },
});
